import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Cookie, SlidersHorizontal } from "lucide-react";
import { readConsent, saveConsent, DEFAULT_CONSENT } from "@/lib/consent";

const OPTIONS = [
  { key: "necessary", label: "Necessari", desc: "Indispensabili al funzionamento del sito. Sempre attivi.", locked: true },
  { key: "statistics", label: "Statistici", desc: "Misurano le visite in forma aggregata. Nessuno attivo oggi." },
  { key: "marketing", label: "Marketing", desc: "Annunci, anche personalizzati. Nessuno attivo oggi." },
];

export function CookieBanner() {
  const [open, setOpen] = useState(false);
  const [detail, setDetail] = useState(false);
  const [grants, setGrants] = useState(DEFAULT_CONSENT);

  useEffect(() => {
    const stored = readConsent();
    if (!stored) setOpen(true);
    else setGrants({ necessary: true, statistics: stored.statistics, marketing: stored.marketing });
    const reopen = () => {
      const c = readConsent();
      if (c) setGrants({ necessary: true, statistics: c.statistics, marketing: c.marketing });
      setDetail(true);
      setOpen(true);
    };
    window.addEventListener("cpt-open-cookie-preferences", reopen);
    return () => window.removeEventListener("cpt-open-cookie-preferences", reopen);
  }, []);

  const decide = (next) => { saveConsent(next); setOpen(false); setDetail(false); };

  if (!open) return null;

  return (
    <div data-testid="cookie-banner" role="dialog" aria-label="Preferenze cookie"
      className="fixed inset-x-0 bottom-0 z-50 p-3 sm:p-5">
      <div className="max-w-3xl mx-auto rounded-3xl bg-panel border-2 border-stone-900 overflow-hidden shadow-[0_10px_0_0_rgba(28,25,23,0.9)]">
        <div className="p-5 sm:p-6">
          <div className="flex items-center gap-2 text-gold mb-2">
            <Cookie size={16} />
            <span className="font-meta text-[10px] uppercase tracking-[0.22em]">Cookie & privacy</span>
          </div>
          <h2 className="font-editorial font-extrabold text-lg md:text-lg text-stone-900">
            Qui dentro <span className="mark-hl">nessun tracciamento</span>
          </h2>
          <p className="text-sm text-stone-600 mt-2 leading-relaxed">
            Usiamo solo strumenti tecnici necessari a far funzionare il sito. Statistiche e pubblicità sono
            disattivate e nessuno script di terze parti viene caricato senza il tuo consenso. Dettagli nella{" "}
            <Link to="/cookie" className="font-semibold text-stone-900 underline decoration-gold decoration-2 underline-offset-2">cookie policy</Link>{" "}
            e nell'<Link to="/privacy" className="font-semibold text-stone-900 underline decoration-gold decoration-2 underline-offset-2">informativa privacy</Link>.
          </p>

          {detail && (
            <div className="mt-4 space-y-2" data-testid="cookie-detail">
              {OPTIONS.map((o) => (
                <label key={o.key} data-testid={`cookie-toggle-${o.key}`}
                  className={`flex items-start gap-3 rounded-2xl border-2 border-line p-3.5 ${o.locked ? "opacity-70" : "cursor-pointer hover:border-stone-300"}`}>
                  <input type="checkbox" className="mt-0.5 w-4 h-4 accent-stone-900"
                    checked={o.locked ? true : grants[o.key]} disabled={o.locked}
                    onChange={(e) => setGrants((p) => ({ ...p, [o.key]: e.target.checked }))} />
                  <span>
                    <span className="block text-sm font-bold text-stone-900">{o.label}</span>
                    <span className="block text-xs text-stone-500 mt-0.5">{o.desc}</span>
                  </span>
                </label>
              ))}
            </div>
          )}

          <div className="mt-5 flex flex-col sm:flex-row gap-2.5">
            <button type="button" data-testid="cookie-accept-all"
              onClick={() => decide({ necessary: true, statistics: true, marketing: true })}
              className="px-6 py-3 rounded-full text-sm font-bold bg-stone-900 text-white sticker hover:-translate-y-0.5 transition-transform">
              Accetta tutti
            </button>
            <button type="button" data-testid="cookie-reject"
              onClick={() => decide({ necessary: true, statistics: false, marketing: false })}
              className="px-6 py-3 rounded-full text-sm font-bold bg-ink border-2 border-line text-stone-800 hover:border-stone-900 transition-colors">
              Solo necessari
            </button>
            {detail ? (
              <button type="button" data-testid="cookie-save"
                onClick={() => decide(grants)}
                className="px-6 py-3 rounded-full text-sm font-bold bg-ink border-2 border-line text-stone-800 hover:border-stone-900 transition-colors">
                Salva le mie scelte
              </button>
            ) : (
              <button type="button" data-testid="cookie-customize" onClick={() => setDetail(true)}
                className="px-6 py-3 rounded-full text-sm font-semibold text-stone-500 hover:text-stone-900 transition-colors flex items-center justify-center gap-2">
                <SlidersHorizontal size={14} /> Personalizza
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
